/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable max-len */
import { Button, Checkbox, Col, Form, Radio, Row, Space } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { EStavLeiPlatba, EStavLeiPlatby, ILeiData, ILeiDetailData } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import leiAktualizace from 'services/lei/leiAktualizace';
import leiDetailApi from 'services/lei/leiDetail';
import { EStavZadostiLoginlessLei } from 'services/lei/leiLoginLess';
import leiDataApi from 'services/lei/novyLeiData';
import { IZalozeniLeiResp } from 'services/lei/zalozeniLei';

import { ETypMatky } from 'enums/lei/leiDataEnums';
import { EJinyTyp } from 'enums/lei/leiJinyTypEnums';
import { EMaterskaSpol } from 'enums/lei/materskaSpol';
import { ETypAdresy } from 'enums/lei/typAdresy';
import { ENotificationType } from 'enums/notificationType';
import routePathEnum from 'enums/routePathEnum';

import DetailInfoPrehled from './PrehledPages/DetailInfoPrehled';
import FinalniMatkaPrehled from './PrehledPages/FinalniMatkaPrehled';
import OsobaPrehled from './PrehledPages/OsobaPrehled';
import PrimaMatkaPrehled from './PrehledPages/PrimaMatkaPrehled';
import ProfondyPrehled from './PrehledPages/ProfondyPrehled';
import UpravaLeiDetail from './upravaLeiDetail';

interface IPrehled {
    formData?: ILeiData;
    idVarsymZadosti?: IZalozeniLeiResp;
    prevStep?: () => void;
    setCurrentStep?: React.Dispatch<React.SetStateAction<number>>;
    isAktualizace?: boolean;
    PrehledOnly?: boolean;
    setShowTitle?: React.Dispatch<React.SetStateAction<boolean>>;
}

const Prehled: React.FC<IPrehled> = ({
    prevStep,
    formData,
    idVarsymZadosti,
    isAktualizace,
    PrehledOnly,
    setShowTitle,
    setCurrentStep
}) => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const [form] = Form.useForm();
    const { setLevel } = useProfily();
    const { cisStaty, aktivniFirma, NotificationComponent, lei, platbaLei, language } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const [consentChecked, setConsentChecked] = useState<boolean>(false);
    const [showPaymentButton, setShowPaymentButton] = useState<boolean>(!isAktualizace);
    const [showPaymentPage, setShowPaymentPage] = useState<boolean>(false);
    const setForceRerender = useState<boolean>(false)[1];
    const { FIRMA_PLAIN, LEI_PLATBA, LEI_DETAIL } = routePathEnum;
    const baseURL = `${FIRMA_PLAIN}${aktivniFirma?.iducet}`;
    const jeCZ =
        formData?.spolecnost?.adresy?.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0].country === 'CZ' ||
        lei?.detailData?.spolecnost?.adresy?.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0].country === 'CZ' ||
        lei?.data?.spolecnost?.adresy?.filter(e => e.typadresy === ETypAdresy.LEGALADDRESS)[0].country === 'CZ';
    const rerender = () => {
        setForceRerender(val => !val);
    };

    useEffect(() => {
        showPaymentPage && setShowTitle && setShowTitle(false);
        (lei?.detailData?.platba?.typplatby === 'KARTOU' || lei?.data?.platba?.typplatby === 'KARTOU') &&
            form.setFieldsValue({
                mena: isAktualizace ? lei?.detailData?.platba?.mena : lei?.data?.platba?.mena,
                typplatby: isAktualizace ? lei?.detailData?.platba?.typplatby : lei?.data?.platba?.typplatby
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showPaymentPage, lei]);

    useEffect(() => {
        setTimeout(() => {
            rerender();
        }, 0); // rerender po nastaveni default values (inak sa niektore nezobrazia)
        if (
            isAktualizace &&
            (!platbaLei ||
                !platbaLei?.stavplatby ||
                ![EStavLeiPlatby.PLZRUS, EStavLeiPlatby.CEKA, EStavLeiPlatby.PLZAPL].includes(platbaLei?.stavplatby))
        ) {
            setLoading(true);
            leiDetailApi(lei?.idVarsymZadosti?.varsym || '0')
                .then(
                    resp =>
                        (resp.stavleiplatba === EStavLeiPlatba.VRACENO ||
                            resp.stavleiplatba === EStavLeiPlatba.ZRUSENO ||
                            resp.stavleiplatba === EStavLeiPlatba.ZAPLATIT) &&
                        setShowPaymentButton(true)
                )
                .finally(() => setLoading(false));
        }
        if (platbaLei) {
            platbaLei.stavplatby === EStavLeiPlatby.PLZRUS || platbaLei.stavplatby === EStavLeiPlatby.CEKA
                ? setShowPaymentPage(true)
                : platbaLei.stavplatby === EStavLeiPlatby.PLZAPL
                  ? platbaLei.token &&
                    NotificationComponent({
                        title: isAktualizace ? 'ZADOST_O_LEI_UPDATE' : 'ZADOST_O_LEI',
                        description: 'LEI_PAYMENT_SUCCESS',
                        type: ENotificationType.SUCCESS
                    })
                  : null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const primaMatkaData = formData?.matky?.length
        ? formData?.matky?.filter(
              e =>
                  e.typmatky === ETypMatky.IS_DIRECTLY_CONSOLIDATED_BY ||
                  e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF
          )
        : undefined;
    const internationalMatkaData = formData?.matky?.length
        ? formData?.matky?.filter(e => e.typmatky === ETypMatky.IS_INTERNATIONAL_BRANCH_OF)
        : undefined;
    const finalniMatkaArr = formData?.matky?.length
        ? formData?.matky?.filter(e => e.typmatky === ETypMatky.IS_ULTIMATELY_CONSOLIDATED_BY)
        : undefined;
    const finalniMatkaData = finalniMatkaArr && finalniMatkaArr.length > 0 ? finalniMatkaArr : undefined;

    const submitFunc = (vals?: any) => {
        setLoading(true);
        isAktualizace
            ? (lei?.detailData?.platba?.idplatby || lei?.data?.platba?.idplatby) && vals.typplatby === 'KARTOU'
                ? window.location.replace(
                      `https://payments.comgate.cz/specify/card/${lei?.detailData?.platba?.idplatby}`
                  )
                : leiAktualizace(
                      'post',
                      idVarsymZadosti?.varsym ? idVarsymZadosti.varsym : '0',
                      idVarsymZadosti?.id ? idVarsymZadosti.id : 0,
                      vals
                          ? {
                                ...formData,
                                ...(lei?.detailData?.platbaspe && {
                                    platbaspe: lei?.detailData?.platbaspe
                                }),
                                language: language,
                                platba: {
                                    ...(vals.mena &&
                                        vals.typplatby === 'KARTOU' && {
                                            mena: vals.mena
                                        }),
                                    ...(vals.typplatby === 'KARTOU' && {
                                        castka:
                                            form.getFieldValue('mena') === 'CZK'
                                                ? lei?.selectedLei?.stavlei
                                                    ? jeCZ
                                                        ? 1373.0
                                                        : 1135.0
                                                    : jeCZ
                                                      ? 1875.0
                                                      : 1550.0
                                                : lei?.selectedLei?.stavlei
                                                  ? jeCZ
                                                      ? 54.5
                                                      : 45
                                                  : jeCZ
                                                    ? 78.5
                                                    : 65
                                    }),
                                    typplatby: vals.typplatby
                                }
                            }
                          : {
                                ...formData,
                                language: language
                            }
                  )
                      .then(resp => {
                          vals && vals.typplatby === 'KARTOU'
                              ? resp.url && (window.location.replace(resp.url), setLoading(false))
                              : (() =>
                                    leiDetailApi(lei?.idVarsymZadosti?.varsym || '0')
                                        .then(responzo => {
                                            setLevel('lei', {
                                                ...lei,
                                                detailData: responzo,
                                                paymentDueDate: responzo.paymentduedate
                                            });
                                            NotificationComponent({
                                                title: isAktualizace ? 'ZADOST_O_LEI_UPDATE' : 'ZADOST_O_LEI',
                                                description: !isAktualizace
                                                    ? 'ZADOST_ODESLANA_LEI'
                                                    : 'ZADOST_ODESLANA_LEI_AKTUALIZACE',
                                                type: ENotificationType.SUCCESS
                                            });
                                            if (vals && vals.typplatby === 'PREVODEM') {
                                                vals.typplatby !== 'KARTOU' &&
                                                    navigateWorkaround(`${baseURL}${LEI_PLATBA}`);
                                            } else {
                                                navigateWorkaround(`${baseURL}${LEI_DETAIL}`);
                                            }
                                        })
                                        .catch(() => {
                                            navigateWorkaround(`${baseURL}${LEI_DETAIL}`);
                                        })
                                        .finally(() => {
                                            setLoading(false);
                                        }))();
                      })
                      .catch(() => {})
                      .finally(() => setLoading(false))
            : lei?.data?.platba?.idplatby && vals.typplatby === 'KARTOU'
              ? window.location.replace(`https://payments.comgate.cz/specify/card/${lei?.data?.platba?.idplatby}`)
              : leiDataApi('post', idVarsymZadosti?.id || 0, idVarsymZadosti?.varsym || '0', {
                    ...formData,
                    language: language,
                    ...(vals && {
                        platba: {
                            ...(vals.mena && {
                                mena: vals.mena
                            }),
                            castka:
                                form.getFieldValue('mena') === 'CZK'
                                    ? lei?.selectedLei?.stavlei
                                        ? jeCZ
                                            ? 1373.0
                                            : 1135.0
                                        : jeCZ
                                          ? 1875.0
                                          : 1550.0
                                    : lei?.selectedLei?.stavlei
                                      ? jeCZ
                                          ? 54.5
                                          : 45
                                      : jeCZ
                                        ? 78.5
                                        : 65,
                            typplatby: vals.typplatby
                        }
                    })
                })
                    .then(responzo => {
                        vals.typplatby === 'KARTOU'
                            ? responzo.url && (window.location.replace(responzo.url), setLoading(false))
                            : setLevel('lei', {
                                  ...lei,
                                  detailData: {
                                      ...responzo,
                                      ...formData
                                  },
                                  paymentDueDate: dayjs().add(7, 'd').format('YYYY-MM-DD') // nie je realna hodnota splatnosti ale skratka ktora umozni zobrazenie (neskor bude chodit realna hodnota)
                              });
                        vals.typplatby !== 'KARTOU' &&
                            NotificationComponent({
                                title: isAktualizace ? 'ZADOST_O_LEI_UPDATE' : 'ZADOST_O_LEI',
                                description: !isAktualizace ? 'ZADOST_ODESLANA_LEI' : 'ZADOST_ODESLANA_LEI_AKTUALIZACE',
                                type: ENotificationType.SUCCESS
                            });
                        vals.typplatby !== 'KARTOU' && navigateWorkaround(`${baseURL}${LEI_PLATBA}`);
                    })
                    .finally(() => {
                        setLoading(false);
                    });
    };

    return lei?.loginless?.osoba?.stavzadosti === EStavZadostiLoginlessLei.HOTOVO ||
        platbaLei?.stavplatby === EStavLeiPlatby.PLZAPL ? (
        <Title
            level={5}
            text="ZADOST_HOTOVO"
            style={{
                marginTop: 20
            }}
        />
    ) : (
        <LoadingWrapper loading={loading}>
            {showPaymentPage ? (
                <FormWrapper outForm={form} onFinish={vals => submitFunc(vals)}>
                    <Title
                        level={5}
                        dividerTitle={true}
                        text="LEI_PAYMENT_TITLE"
                        style={{
                            marginTop: 20
                        }}
                    />
                    <Form.Item
                        style={{
                            marginBottom: 8
                        }}
                        name="typplatby"
                        rules={[
                            {
                                required: true,
                                message: <TranslateText>NEVALIDNI_POVINNA_POLOZKA</TranslateText>
                            }
                        ]}
                        //  initialValue="PREVODEM"
                    >
                        <Radio.Group
                            style={{
                                marginBottom: 26
                            }}
                            onChange={ev => {
                                form.setFieldsValue({
                                    typplatby: ev.target.value
                                });
                                rerender();
                            }}
                            defaultValue={
                                lei?.detailData?.platba?.typplatby ||
                                lei?.data?.platba?.typplatby ||
                                form.getFieldValue('typplatby')
                            }
                        >
                            <Space direction="vertical">
                                <Radio value={'KARTOU'}>
                                    <TranslateText>LEI_PAYMENT_ONLINE</TranslateText>
                                </Radio>

                                <Radio value={'PREVODEM'}>
                                    <TranslateText>LEI_PAYMENT_TRANSFER</TranslateText>
                                </Radio>
                            </Space>
                        </Radio.Group>
                        {form.getFieldValue('typplatby') === 'KARTOU' && (
                            <SelectComponent
                                isFormItem={true}
                                name="mena"
                                labelParagraph="MENA"
                                defaultValue={platbaLei?.mena}
                                onChange={rerender}
                                data={[
                                    {
                                        value: 'CZK',
                                        labelcz: 'CZK'
                                    },
                                    {
                                        value: 'EUR',
                                        labelcz: 'EUR'
                                    }
                                ]}
                            />
                        )}
                        {form.getFieldValue('mena') && form.getFieldValue('typplatby') === 'KARTOU' && (
                            <div
                                style={{
                                    position: 'relative',
                                    top: -14
                                }}
                            >
                                <Paragraph
                                    text={
                                        form.getFieldValue('mena') === 'CZK'
                                            ? 'LEI_PLATOBNE_INFO_AMOUNTCR'
                                            : 'LEI_PLATOBNE_INFO_AMOUNTSK'
                                    }
                                    style={{
                                        fontWeight: 600,
                                        display: 'block'
                                    }}
                                />
                                <Paragraph
                                    text={
                                        form.getFieldValue('mena') === 'CZK'
                                            ? lei?.selectedLei?.stavlei &&
                                              !['CANCELLED', 'PENDING_ARCHIVAL', 'PENDING_VALIDATION'].includes(
                                                  lei?.selectedLei?.stavlei
                                              )
                                                ? jeCZ
                                                    ? '1373,00'
                                                    : '1135,00'
                                                : jeCZ
                                                  ? '1875,00'
                                                  : '1 550,00'
                                            : lei?.selectedLei?.stavlei &&
                                                !['CANCELLED', 'PENDING_ARCHIVAL', 'PENDING_VALIDATION'].includes(
                                                    lei?.selectedLei?.stavlei
                                                )
                                              ? jeCZ
                                                  ? '54,5'
                                                  : '45'
                                              : jeCZ
                                                ? '78,5'
                                                : '65'
                                    }
                                />
                            </div>
                        )}
                    </Form.Item>
                    {form.getFieldValue('typplatby') && (
                        <Row
                            style={{
                                marginBottom: 16,
                                marginTop: 20
                            }}
                        >
                            <Col>
                                <Checkbox
                                    onChange={() => setConsentChecked(pv => !pv)}
                                    style={{
                                        position: 'relative',
                                        top: -2,
                                        left: 4,
                                        transform: 'scale(1.2)',
                                        marginRight: 16
                                    }}
                                    checked={consentChecked}
                                    id="souhlasCheckbox"
                                />
                                <Paragraph text="LEI_SUBMIT_APPROVAL" />
                            </Col>
                        </Row>
                    )}
                    <Row className="button-row">
                        {!platbaLei && (
                            <ButtonComponent
                                htmlType="button"
                                type="default"
                                buttontext="ZPET"
                                id="platbaZpetBtn"
                                onClick={() => setShowPaymentPage(false)}
                            />
                        )}
                        {form.getFieldValue('typplatby') && (
                            <ButtonComponent
                                id="zaplatitBtn"
                                type="primary"
                                htmlType="submit"
                                disabled={!consentChecked}
                                loading={loading}
                                buttontext={
                                    form.getFieldValue('typplatby') === 'KARTOU' ? 'ZAPLATIT' : 'ODESLAT_ZADOST'
                                }
                            />
                        )}
                        {form.getFieldValue('typplatby') === 'KARTOU' && (
                            <Paragraph
                                text={'PLATEBNI_BRANA_REDIRECT'}
                                style={{
                                    display: 'block',
                                    marginLeft: 10,
                                    marginTop: 6,
                                    maxWidth: 600
                                }}
                            />
                        )}
                    </Row>
                    {form.getFieldValue('typplatby') && (
                        <Paragraph
                            text="LEI_SUBMIT_INFO"
                            style={{
                                display: 'block',
                                marginTop: 16
                            }}
                        />
                    )}
                </FormWrapper>
            ) : (
                <>
                    <Title level={5} dividerTitle={true} text="LEI_AKTUALIZACE_INFORMACE_OPR" />
                    <OsobaPrehled formDataOsoba={formData?.osoba} />
                    <Title
                        level={5}
                        dividerTitle={true}
                        text={isAktualizace ? 'LEI_DETAIL_INFO_TITLE_TABLE' : 'LEI_DETAIL_INFO_TITLE'}
                        style={{
                            marginTop: 10
                        }}
                    />
                    <DetailInfoPrehled formDataSpolecnost={formData?.spolecnost} typZadosti={formData?.typzadosti} />
                    {isAktualizace && (
                        <UpravaLeiDetail
                            nextStep={() => {}}
                            prevStep={() => {}}
                            setCurrentStep={() => {}}
                            formData={formData}
                            setFormData={() => {}}
                            setIsEdit={() => {}}
                            isPrehled={true}
                            PrehledOnly={PrehledOnly}
                            cisStaty={cisStaty}
                        />
                    )}
                    <Title
                        level={5}
                        dividerTitle={true}
                        text={
                            formData?.typzadosti === 'BRANCH' ? 'LEI_DETAIL_MAT_TITLE_BRANCH' : 'LEI_DETAIL_MAT_TITLE'
                        }
                        style={{
                            marginTop: 10
                        }}
                    />
                    <PrimaMatkaPrehled
                        cisStaty={cisStaty}
                        primaMatkaData={primaMatkaData}
                        upravaForm={form}
                        internationalMatkaData={internationalMatkaData}
                    />
                    {formData?.typzadosti !== 'BRANCH' && (
                        <>
                            <Title
                                level={5}
                                dividerTitle={true}
                                text="LEI_FINAL_TITLE"
                                style={{
                                    marginTop: 10
                                }}
                            />

                            <FinalniMatkaPrehled
                                cisStaty={cisStaty}
                                finalniMatkaData={finalniMatkaData}
                                upravaForm={form}
                                primaMatkaStav={
                                    (primaMatkaData && primaMatkaData.length && primaMatkaData[0].stav) || undefined
                                }
                                disabled={primaMatkaData && primaMatkaData[0].stav === EMaterskaSpol.NE}
                            />
                        </>
                    )}
                    {formData?.typzadosti === 'FUND' && (
                        <>
                            <Title level={5} dividerTitle={true} text="LEI_FOND_TITLE" />
                            <ProfondyPrehled vsechnyMatky={formData.matky} />
                        </>
                    )}
                    {prevStep && !PrehledOnly ? (
                        <>
                            {!showPaymentButton && (
                                <Row>
                                    <Col>
                                        <Checkbox
                                            onChange={() => setConsentChecked(pv => !pv)}
                                            style={{
                                                position: 'relative',
                                                top: -2,
                                                left: 4,
                                                transform: 'scale(1.2)',
                                                marginRight: 16
                                            }}
                                            checked={consentChecked}
                                        />
                                        <Paragraph text="LEI_SUBMIT_APPROVAL" />
                                    </Col>
                                </Row>
                            )}
                            <Row className="button-row">
                                <Button
                                    onClick={() => {
                                        const zruseniEvent = (formData as ILeiDetailData)?.events?.filter(
                                            e => e.ostatnievent === EJinyTyp.DISSOLUTION
                                        );
                                        if (
                                            setCurrentStep &&
                                            zruseniEvent?.length &&
                                            dayjs(zruseniEvent[0]?.datumzmeny) < dayjs()
                                        ) {
                                            setCurrentStep(1);
                                        } else {
                                            prevStep();
                                        }
                                    }}
                                    disabled={loading}
                                    id="prehledZpetBtn"
                                >
                                    <TranslateText>ZPET</TranslateText>
                                </Button>
                                <Button
                                    id="prehledOdeslatBtn"
                                    type="primary"
                                    disabled={!showPaymentButton ? !consentChecked : false}
                                    loading={loading}
                                    onClick={() => {
                                        if (showPaymentButton) {
                                            setShowPaymentPage(true);
                                        } else {
                                            submitFunc();
                                        }
                                    }}
                                >
                                    <TranslateText>
                                        {showPaymentButton ? 'LEI_PAYMENT_BUTTON' : 'ODESLAT'}
                                    </TranslateText>
                                </Button>
                            </Row>
                            {!showPaymentButton && <Paragraph text="LEI_SUBMIT_INFO" />}
                        </>
                    ) : (
                        <Row className="button-row">
                            <Button
                                onClick={() => navigateWorkaround(`${baseURL}${LEI_DETAIL}`)}
                                disabled={loading}
                                id="prehledZpetBtn"
                            >
                                <TranslateText>ZPET</TranslateText>
                            </Button>
                        </Row>
                    )}
                </>
            )}
        </LoadingWrapper>
    );
};

export default Prehled;
